/* DATA EXPLORER — a Power-BI-like report builder inside the portal (2026-10-22).

   Max: "thêm bộ công cụ như power BI để user có thể tự chỉnh theo insight của họ", then, on the first build (one
   question at a time): "tôi muốn giao diện nó gần giống với power BI á, kiểu mình sẽ làm được nhiều biểu đồ trên 1 page
   rồi 1 dashboard sẽ có nhiều page". So it is laid out as Power BI is: a REPORT of PAGES, each page a canvas of VISUALS
   (cards, tables, matrices, charts, slicers) placed and sized by dragging, the three panes on the right (Filters ·
   Visualizations · Data), the page tabs under the canvas, and an Edit / Reading switch.

   Every visual is ONE question to /api/explorer/query: its own boxes, the report's filters, its page's, its own, every
   other slicer on the page, and the point somebody clicked on another visual (the cross-filter). The server answers
   cells AND totals (GROUPING SETS), so a total is never the sum of cells that cannot be summed. A report is kept whole
   as JSON (00_PORTAL_explorer_view) and opened by its link (?report=ID); the screen opens on the list of reports this
   person may open — their own and the ones shared with them (people, departments, entities or everybody) — and only
   somebody holding the Data Explorer grant builds one; the others read. The screen knows only what
   /api/explorer/datasets tells it: a clean table added on the server later is a dataset, nothing here. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ellipsis, Loader2, Minimize2, Plus, Redo2, RefreshCw, Undo2, X } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import { useTheme } from './ThemeContext';
import {
  CANVAS_W, GRAINS, MAP_FIELDS, MAX_PAGES, MAX_VISUALS, MIN_H, MIN_W, SNAP, VISUAL_TYPES, defaultSort, fmtDay, freeSpot, keyLabel,
  newPage, newVisual, pageHeight, relLabel, sameDim, sortRows, starterReport, store, uid, visualQuery, wellLimit,
} from './explorerModel';
import { Visual } from './ExplorerVisual';
import { ConfirmDialog, DataPane, FilterEditor, FiltersPane, NameDialog, ReportsBar, VisualsPane } from './ExplorerPanes';
import { ExplorerHome, ShareDialog } from './ExplorerHome';
import AiInsightPanel, { askInsight, INSIGHTS_ON, INSIGHTS_OFF_MARK } from './AiInsightPanel';
import './SPHub.css';
import './ProjectApproval.css';
import './DataExplorer.css';

const API = `${BACKEND_URL}/api/explorer`;
/* A page of eight visuals asks eight questions; four at a time keeps one page from holding the database to itself. */
const CONCURRENT = 6;
const call = async (path, { method = 'GET', body } = {}) => {
  const res = await fetch(`${API}${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
  return j;
};
const snap = (n) => Math.round(n / SNAP) * SNAP;
const MIN_DRAW = 760;
const useOutside = (open, ref, close) => useEffect(() => {
  if (!open) return undefined;
  const down = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
  const key = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('mousedown', down);
  document.addEventListener('keydown', key);
  return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
}, [open, ref, close]);

/* THE GUIDE (Max 01/11: every module has one). It walks the list the screen opens on. New report and the Share / Rename /
   Delete buttons exist only for people who may build reports, and a row's buttons only when there is a row, so those
   steps are skipped by TourGuide when they are not drawn. */
const TOUR = [
  { selector: '.dx-home-tabs', title: 'tourExplorer.tabsT', body: 'tourExplorer.tabsB' },
  { selector: '.dx-home-search', title: 'tourExplorer.searchT', body: 'tourExplorer.searchB' },
  { selector: '.dx-home-bar .pa-btn.primary', title: 'tourExplorer.newT', body: 'tourExplorer.newB' },
  { selector: '.dx-home-table', title: 'tourExplorer.listT', body: 'tourExplorer.listB' },
  { selector: '.dx-home-row .dx-acts', title: 'tourExplorer.actionsT', body: 'tourExplorer.actionsB' },
];

export default function DataExplorer() {
  const { t } = useTranslation();
  const { resolved: theme } = useTheme();
  const [params, setParams] = useSearchParams();
  const [metaList, setMetaList] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [me, setMe] = useState(null); // { uid, name, builder } — builder = holds the grant; the others read
  const [ready, setReady] = useState(false);
  const [shareOf, setShareOf] = useState(null); // the saved report the Share dialog is open on
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [current, setCurrent] = useState(null); // { id, name, mine, shared, json } of the saved report on screen
  const [editing, setEditing] = useState(true);
  const [pageId, setPageId] = useState(null);
  const [selId, setSelId] = useState(null);
  const [xf, setXf] = useState(null); // the cross-filter: { pageId, source, key, filters }
  const [focusId, setFocusId] = useState(null);
  const [drag, setDrag] = useState(null); // the visual under the hand: { id, x, y, w, h }
  /* The three panes take ~700px; on a screen under 1800px the Filters pane starts folded so the page keeps room. */
  const [panes, setPanes] = useState(() => {
    const first = { filters: window.innerWidth >= 1800, vis: true, data: true };
    try { return { ...first, ...JSON.parse(localStorage.getItem('dxPanes') || '{}') }; } catch { return first; }
  });
  const [filterEdit, setFilterEdit] = useState(null); // { level, index, field }
  const [dialog, setDialog] = useState(null);
  const [pageMenu, setPageMenu] = useState(null); // { id, x, y } — the page the menu acts on and where it opens
  const [flash, setFlash] = useState('');
  const [err, setErr] = useState('');
  const [width, setWidth] = useState(0);
  const [tick, setTick] = useState(0);
  const cache = useRef(new Map());
  const inflight = useRef(new Set());
  const lastByVisual = useRef(new Map());
  const valuesCache = useRef(new Map());
  const geoCache = useRef(new Map());
  const sizerRef = useRef(null);
  const pageMenuRef = useRef(null);
  const closePageMenu = useCallback(() => setPageMenu(null), []);
  useOutside(pageMenu, pageMenuRef, closePageMenu);

  const meta = useMemo(() => metaList?.find((d) => d.key === report?.dataset) || null, [metaList, report?.dataset]);
  const fields = useMemo(() => meta?.fields || [], [meta]);
  const fieldOf = useCallback((key) => fields.find((f) => f.key === key) || null, [fields]);
  const fieldLabel = useCallback((key) => t(`xExplorer.f_${key}`, { defaultValue: key }), [t]);
  const dimLabel = useCallback((d) => (d.grain ? `${fieldLabel(d.field)} · ${t(`xExplorer.g_${d.grain}`)}` : fieldLabel(d.field)), [fieldLabel, t]);
  const currency = report?.params?.currency || '';
  const valueLabel = useCallback((v) => {
    /* A value renamed for this visual reads by its own name everywhere the visual prints it. */
    if (v.label && String(v.label).trim()) return String(v.label).trim();
    const f = fieldOf(v.field);
    const agg = f?.aggs && f.aggs.length > 1 && v.agg && v.agg !== f.defaultAgg ? ` (${t(`xExplorer.a_${v.agg}`)})` : '';
    /* A comparison says what it is beside the field: "Revenue · Last year", "Revenue · vs last year %". A percentage
       carries no currency. */
    const calc = v.calc ? ` · ${t(`xExplorer.calc_${v.calc}`)}` : '';
    const pct = v.calc === 'yoy_pct' || v.calc === 'mom_pct' || v.calc === 'share';
    return `${fieldLabel(v.field)}${agg}${calc}${f?.fmt === 'money' && currency && !pct ? ` · ${currency}` : ''}`;
  }, [fieldOf, fieldLabel, t, currency]);
  /* A visual with no title of its own is named by what it shows, as Power BI names one: "Revenue by Month and Entity". */
  const autoTitle = useCallback((v) => {
    if (v.type === 'slicer') return v.rows[0] ? fieldLabel(v.rows[0].field) : t('xExplorer.vt_slicer');
    const values = [...v.values, ...(v.values2 || [])]
      .map((x) => (x.calc ? `${fieldLabel(x.field)} · ${t(`xExplorer.calc_${x.calc}`)}` : fieldLabel(x.field))).join(', ');
    if (!values) return t(`xExplorer.vt_${v.type}`);
    if (v.type === 'kpi') return t('xExplorer.kpiTitle', { value: values });
    const withCols = VISUAL_TYPES[v.type]?.wells.some((w) => w[0] === 'cols');
    const dims = [...(v.type === 'card' ? [] : v.rows), ...(withCols ? v.cols : [])]
      .map((d) => (d.grain ? t(`xExplorer.g_${d.grain}`) : fieldLabel(d.field)));
    return dims.length ? t('xExplorer.titleBy', { values, dims: dims.join(t('xExplorer.titleAnd')) }) : values;
  }, [fieldLabel, t]);
  const filterSummary = useCallback((f, short) => {
    const field = f.grain ? `${fieldLabel(f.field)} · ${t(`xExplorer.g_${f.grain}`)}` : fieldLabel(f.field);
    if (f.op === 'relative') return short ? relLabel(f, t) : t('xExplorer.sumRel', { field, period: relLabel(f, t) });
    if (f.op === 'between') {
      if (short) {
        if (f.from && f.to) return `${fmtDay(f.from)} – ${fmtDay(f.to)}`;
        if (f.from) return `${t('xExplorer.fFrom')} ${fmtDay(f.from)}`;
        if (f.to) return `${t('xExplorer.fTo')} ${fmtDay(f.to)}`;
        return t('xExplorer.fAll');
      }
      if (f.from && f.to) return t('xExplorer.sumRange', { field, from: fmtDay(f.from), to: fmtDay(f.to) });
      if (f.from) return t('xExplorer.sumFrom', { field, from: fmtDay(f.from) });
      if (f.to) return t('xExplorer.sumTo', { field, to: fmtDay(f.to) });
      return t('xExplorer.sumAll', { field });
    }
    if (f.op === 'contains') return short ? `${t('xExplorer.fContains')} “${f.text}”` : t('xExplorer.sumContains', { field, text: f.text });
    const vals = (f.values || []).map((x) => keyLabel({ grain: f.grain }, x));
    const values = vals.slice(0, 2).join(', ') + (vals.length > 2 ? ` ${t('xExplorer.sumMore', { n: vals.length - 2 })}` : '');
    if (short) return f.op === 'not_in' ? `${t('xExplorer.fExclude')}: ${values}` : values;
    return t(f.op === 'not_in' ? 'xExplorer.sumNotIn' : 'xExplorer.sumIn', { field, values });
  }, [fieldLabel, t]);

  /* ------------------------------------------------ the saved reports */
  const say = useCallback((msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); }, []);
  const reloadReports = useCallback(async () => { try { setReports((await call('/views')).views); } catch { /* the list on screen stays */ } }, []);
  const open = useCallback((saved) => {
    const r = { ...saved.spec, name: saved.name };
    setReport(r);
    setPageId(r.pages[0]?.id || null);
    setSelId(null);
    setXf(null);
    setFocusId(null);
    setCurrent({ id: saved.id, name: saved.name, mine: saved.mine, canEdit: !!(saved.mine || saved.canEdit), shared: saved.shared, ownerName: saved.ownerName, json: JSON.stringify(r) });
    hist.current = { past: [], future: [], tag: null, at: 0 };
    /* Somebody else's shared report opens to be read; one's own opens to be worked on. */
    setEditing(!!(saved.mine || saved.canEdit) && !!me?.builder);
    if (params.get('report') !== String(saved.id)) setParams({ report: String(saved.id) });
  }, [setParams, params, me]);

  /* ------------------------------------------------ load: the datasets and the reports this person may open. What is on
     screen then follows the address: no ?report = the list, ?report=ID = that report, ?report=draft = the report being
     built that has not been saved yet (kept in this browser, so a reload or a trip to the list loses nothing). */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [d, v] = await Promise.all([call('/datasets'), call('/views')]);
        if (!live) return;
        setMetaList(d.datasets);
        setMe(d.me || null);
        setReports(v.views);
        setReady(true);
      } catch (e) {
        if (live) setLoadErr(e.message || t('xExplorer.errLoad'));
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const builder = !!me?.builder;
  const wanted = params.get('report') || params.get('view') || '';
  const atHome = !wanted;
  const known = useCallback((key) => !!metaList?.some((x) => x.key === key), [metaList]);
  /* The draft this browser holds: a new report never saved, or a saved one with changes not saved yet. */
  const draftOf = () => {
    const kept = store.get('dxReport');
    if (!kept?.pages?.length || !known(kept.dataset)) return null;
    const keptCur = store.get('dxCurrent');
    if (keptCur?.id) {
      if (!reports.some((x) => x.id === keptCur.id && (x.mine || x.canEdit)) || keptCur.json === JSON.stringify(kept)) return null;
      return { report: kept, current: keptCur, name: keptCur.name };
    }
    return builder ? { report: kept, current: null, name: '' } : null;
  };
  const loadDraft = (dr) => {
    resetHistory();
    setReport(dr.report);
    setCurrent(dr.current);
    setPageId(dr.report.pages[0].id);
    setSelId(null); setXf(null); setFocusId(null);
    setEditing(builder);
  };
  useEffect(() => {
    if (!ready || !metaList?.length || atHome) return;
    if (wanted === 'draft') {
      if (report && !current) return;
      const dr = draftOf();
      if (dr && !dr.current) { loadDraft(dr); return; }
      if (builder) { newReport(); return; }
      setParams({}, { replace: true });
      return;
    }
    const id = Number(wanted) || 0;
    if (report && current?.id === id) return;
    const saved = reports.find((x) => x.id === id);
    if (!saved || !saved.spec?.pages?.length || !known(saved.dataset)) {
      setErr(t('xExplorer.notFound'));
      setParams({}, { replace: true });
      return;
    }
    /* Back on a report whose unsaved changes this browser kept: they come back with it. */
    const dr = draftOf();
    if (dr?.current?.id === id) { loadDraft(dr); return; }
    open(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, wanted, reports]);
  useEffect(() => { if (report && !atHome) store.set('dxReport', report); }, [report, atHome]);
  useEffect(() => { if (!atHome) store.set('dxCurrent', current); }, [current, atHome]);
  useEffect(() => { try { localStorage.setItem('dxPanes', JSON.stringify(panes)); } catch { /* a convenience only */ } }, [panes]);

  /* The page is laid out 1280 wide and drawn at the width the screen leaves it. */
  const hasReport = !!report && !!meta && !atHome;
  useEffect(() => {
    const el = sizerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [hasReport]);

  const page = report?.pages.find((p) => p.id === pageId) || report?.pages[0] || null;
  const sel = page?.visuals.find((v) => v.id === selId) || null;
  const pageXf = xf && xf.pageId === page?.id ? xf : null;
  /* Drawn no narrower than MIN_DRAW: below that a chart's labels and a table's rows stop fitting their boxes, and a
     page scrolled sideways reads better than a page shrunk past reading. */
  const drawW = Math.max(width, MIN_DRAW);
  const scale = width ? drawW / CANVAS_W : 1;

  /* ------------------------------------------------ changing the report */
  /* Every change goes through `commit`, which keeps the report as it was before it: Undo puts it back, Redo takes it
     forward again. The ref moves at once, so two changes made in one click build on each other. A run of the same
     small change (typing a title) is one step, not one per letter. */
  const reportRef = useRef(report);
  reportRef.current = report;
  const pageIdRef = useRef(pageId);
  pageIdRef.current = pageId;
  const hist = useRef({ past: [], future: [], tag: null, at: 0 });
  const [, setHistTick] = useState(0);
  const resetHistory = () => { hist.current = { past: [], future: [], tag: null, at: 0 }; setHistTick((n) => n + 1); };
  const commit = useCallback((fn, tag) => {
    const prev = reportRef.current;
    if (!prev) return;
    const next = fn(prev);
    if (!next || next === prev) return;
    const h = hist.current;
    const now = Date.now();
    if (!(tag && h.tag === tag && now - h.at < 1500 && h.past.length)) {
      h.past.push(prev);
      if (h.past.length > 100) h.past.shift();
    }
    h.tag = tag || null;
    h.at = now;
    h.future = [];
    reportRef.current = next;
    setReport(next);
    setHistTick((n) => n + 1);
  }, []);
  const editReport = useCallback((fn, tag) => commit(fn, tag), [commit]);
  const editPage = useCallback((fn, tag) => commit((r) => {
    const id = r.pages.some((p) => p.id === pageIdRef.current) ? pageIdRef.current : r.pages[0].id;
    return { ...r, pages: r.pages.map((p) => (p.id === id ? fn(p) : p)) };
  }, tag), [commit]);
  const editVisual = useCallback((id, fn, tag) => editPage((p) => ({ ...p, visuals: p.visuals.map((v) => (v.id === id ? fn(v) : v)) }), tag), [editPage]);
  const travel = useCallback((back) => {
    const h = hist.current;
    const from = back ? h.past : h.future;
    const to = back ? h.future : h.past;
    if (!from.length || !reportRef.current) return;
    to.push(reportRef.current);
    const r = from.pop();
    h.tag = null;
    reportRef.current = r;
    setReport(r);
    if (!r.pages.some((p) => p.id === pageIdRef.current)) setPageId(r.pages[0]?.id || null);
    setSelId((id) => (r.pages.some((p) => p.visuals.some((v) => v.id === id)) ? id : null));
    setXf(null);
    setHistTick((n) => n + 1);
  }, []);
  const canUndo = hist.current.past.length > 0;
  const canRedo = hist.current.future.length > 0;

  const fetchValues = useCallback((field, grain, q) => {
    const key = `${report?.dataset}|${field}|${grain || ''}|${q || ''}`;
    if (!valuesCache.current.has(key)) {
      const p = call(`/values?dataset=${encodeURIComponent(report?.dataset || '')}&field=${encodeURIComponent(field)}${grain ? `&grain=${grain}` : ''}&q=${encodeURIComponent(q || '')}`);
      p.catch(() => valuesCache.current.delete(key));
      valuesCache.current.set(key, p);
    }
    return valuesCache.current.get(key);
  }, [report?.dataset]);

  /* One level down: the next date grain, or the next field of the dimension's hierarchy not already on the visual. */
  const nextLevel = useCallback((d, placed) => {
    const f = fieldOf(d.field);
    if (f?.kind === 'date') {
      const i = GRAINS.indexOf(d.grain);
      return i >= 0 && i < GRAINS.length - 1 ? { field: d.field, grain: GRAINS[i + 1] } : null;
    }
    const h = meta?.hierarchies.find((x) => x.levels.includes(d.field));
    if (!h) return null;
    for (const lv of h.levels.slice(h.levels.indexOf(d.field) + 1)) if (!placed.some((p) => p.field === lv)) return { field: lv };
    return null;
  }, [fieldOf, meta]);

  /* The pointer moves a visual or resizes it. The visual follows the hand through `drag`, and the report changes once,
     on release, so the other visuals do not redraw at every step of the mouse. */
  const startGesture = useCallback((e, id, kind) => {
    const v = page?.visuals.find((x) => x.id === id);
    if (!v || e.button !== 0) return;
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const s = scale || 1;
    let cur = { id, x: v.x, y: v.y, w: v.w, h: v.h };
    const move = (ev) => {
      const dx = (ev.clientX - sx) / s;
      const dy = (ev.clientY - sy) / s;
      cur = kind === 'move'
        ? { ...cur, x: Math.min(Math.max(0, snap(v.x + dx)), CANVAS_W - v.w), y: Math.max(0, snap(v.y + dy)) }
        : { ...cur, w: Math.min(Math.max(MIN_W, snap(v.w + dx)), CANVAS_W - v.x), h: Math.max(MIN_H, snap(v.h + dy)) };
      setDrag(cur);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDrag(null);
      if (cur.x !== v.x || cur.y !== v.y || cur.w !== v.w || cur.h !== v.h) {
        editVisual(id, (x) => ({ ...x, x: cur.x, y: cur.y, w: cur.w, h: cur.h }));
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [page, scale, editVisual]);

  const addVisual = useCallback((type, init = {}) => {
    if (!page) return null;
    if (page.visuals.length >= MAX_VISUALS) { setErr(t('xExplorer.tooManyVisuals', { n: MAX_VISUALS })); return null; }
    const base = newVisual(type, init);
    const v = { ...base, ...freeSpot(page.visuals, base.w, base.h) };
    editPage((p) => ({ ...p, visuals: [...p.visuals, v] }));
    setSelId(v.id);
    return v;
  }, [page, editPage, t]);

  /* Changing a visual's kind keeps what fits the new kind's boxes; a table has no columns, so they join its rows. */
  const fitToType = useCallback((v, type) => {
    const has = (w) => VISUAL_TYPES[type].wells.some((x) => x[0] === w);
    const lim = (w) => wellLimit(type, w);
    let rows = has('rows') ? v.rows.slice(0, lim('rows')) : [];
    /* A map keeps the geographic field it is given, wherever it sat. */
    if (type === 'map') { const geo = [...v.rows, ...v.cols].find((d) => MAP_FIELDS.includes(d.field)); if (geo) rows = [geo]; }
    if (type === 'table' && v.cols.length) rows = [...v.rows, ...v.cols].slice(0, lim('rows'));
    if (type === 'slicer') rows = rows.map((d) => (fieldOf(d.field)?.kind === 'date' ? { field: d.field } : d));
    /* A combo's line values join the rest when it becomes something else; a pie or a KPI keeps one value and drops
       the comparisons it cannot draw. */
    const allValues = [...v.values, ...(v.values2 || [])];
    let values = has('values') ? allValues.slice(0, lim('values')) : [];
    if (type === 'kpi') values = values.map((x) => ({ ...x, calc: undefined }));
    return {
      ...v, type, rows,
      cols: has('cols') ? v.cols.slice(0, lim('cols')) : [],
      values,
      values2: has('values2') ? allValues.slice(values.length, values.length + lim('values2')) : undefined,
      slicer: type === 'slicer' ? v.slicer || null : undefined,
      sort: null,
    };
  }, [fieldOf]);

  /* Export reads the page as it is now, through a ref, so the actions below need not change at every click. */
  const exportRef = useRef(null);
  const actionsNow = useMemo(() => ({
    select: (id) => { if (editing) setSelId(id); },
    startMove: (e, id) => startGesture(e, id, 'move'),
    startResize: (e, id) => startGesture(e, id, 'resize'),
    patch: (id, patch) => editVisual(id, (v) => ({ ...v, ...patch }), 'title' in patch ? `title:${id}` : undefined),
    setType: (id, type) => editVisual(id, (v) => fitToType(v, type)),
    addVisual: (type) => addVisual(type),
    duplicate: (id) => {
      const v = page?.visuals.find((x) => x.id === id);
      if (!v) return;
      if (page.visuals.length >= MAX_VISUALS) { setErr(t('xExplorer.tooManyVisuals', { n: MAX_VISUALS })); return; }
      const copy = { ...JSON.parse(JSON.stringify(v)), id: uid(), x: Math.min(v.x + 16, CANVAS_W - v.w), y: v.y + 16 };
      editPage((p) => ({ ...p, visuals: [...p.visuals, copy] }));
      setSelId(copy.id);
    },
    remove: (id) => {
      editPage((p) => ({ ...p, visuals: p.visuals.filter((v) => v.id !== id) }));
      setSelId((s) => (s === id ? null : s));
      setXf((x) => (x?.source === id ? null : x));
      setFocusId((f) => (f === id ? null : f));
    },
    /* Ticking a field puts it in the selected visual's natural box — a value into Values, anything else onto the axis
       — and, with nothing selected, makes a visual of it: a card for a value, a table for anything else. */
    toggleField: (key) => {
      const f = fieldOf(key);
      if (!f) return;
      const dim = f.kind === 'date' ? { field: key, grain: 'month' } : { field: key };
      if (!sel) {
        if (f.kind === 'measure') addVisual('card', { values: [{ field: key, agg: f.defaultAgg }] });
        else addVisual('table', { rows: [dim] });
        return;
      }
      editVisual(sel.id, (v) => {
        if ([...v.rows, ...v.cols, ...v.values, ...(v.values2 || [])].some((x) => x.field === key)) {
          return {
            ...v, rows: v.rows.filter((x) => x.field !== key), cols: v.cols.filter((x) => x.field !== key),
            values: v.values.filter((x) => x.field !== key), ...(v.values2 ? { values2: v.values2.filter((x) => x.field !== key) } : {}), sort: null,
          };
        }
        if (f.kind === 'measure') {
          const item = { field: key, agg: f.defaultAgg };
          if (v.values.length < wellLimit(v.type, 'values')) return { ...v, values: [...v.values, item] };
          /* A combo's columns are full: the next value is a line. */
          if (v.type === 'combo' && (v.values2 || []).length < wellLimit(v.type, 'values2')) return { ...v, values2: [...(v.values2 || []), item] };
          return v;
        }
        if (v.type === 'slicer') return { ...v, rows: [f.kind === 'date' ? { field: key } : dim], slicer: null };
        if (v.type === 'map') return { ...v, rows: [dim], sort: null };
        /* On a chart the second category becomes the legend, as Power BI does: two on one axis read as "Oct/2026 ·
           Outdoor" under every bar. */
        if (VISUAL_TYPES[v.type]?.chart && wellLimit(v.type, 'cols') > 0 && v.rows.length && !v.cols.length) return { ...v, cols: [dim], sort: null };
        if (v.rows.length < wellLimit(v.type, 'rows')) return { ...v, rows: [...v.rows, dim], sort: null };
        if (v.cols.length < wellLimit(v.type, 'cols')) return { ...v, cols: [...v.cols, dim], sort: null };
        return v;
      });
    },
    dropInWell: (id, well, p, index) => {
      const f = fieldOf(p.field);
      const valueWell = (w) => w === 'values' || w === 'values2';
      if (!f || valueWell(well) !== (f.kind === 'measure')) return;
      if (p.from && valueWell(p.from.well) !== valueWell(well)) return;
      editVisual(id, (v) => {
        let item;
        const next = { ...v };
        if (p.from && p.from.visual === id) {
          item = (v[p.from.well] || [])[p.from.index];
          if (!item) return v;
          next[p.from.well] = (v[p.from.well] || []).filter((_, i) => i !== p.from.index);
        } else if (f.kind === 'measure') item = { field: f.key, agg: f.defaultAgg };
        else if (f.kind === 'date') item = v.type === 'slicer' ? { field: f.key } : { field: f.key, grain: 'month' };
        else item = { field: f.key };
        if (!valueWell(well)) for (const w of ['rows', 'cols']) next[w] = next[w].filter((x) => !sameDim(x, item));
        const list = [...(next[well] || [])];
        list.splice(Math.min(index ?? list.length, list.length), 0, item);
        if (list.length > wellLimit(v.type, well)) return v;
        return { ...next, [well]: list, sort: null, ...(v.type === 'slicer' ? { slicer: null } : {}) };
      });
    },
    setWellItem: (id, well, i, patch) => editVisual(id, (v) => ({
      ...v, [well]: (v[well] || []).map((x, j) => (j === i ? { ...x, ...patch } : x)), sort: null, ...(v.type === 'slicer' ? { slicer: null } : {}),
    })),
    removeWellItem: (id, well, i) => editVisual(id, (v) => ({ ...v, [well]: (v[well] || []).filter((_, j) => j !== i), sort: null })),
    /* A click on a point filters the OTHER visuals of the page; the same click again clears it. */
    point: (id, vals, dims) => {
      const key = JSON.stringify(vals);
      setXf((x) => (x && x.source === id && x.key === key ? null : {
        pageId: page?.id, source: id, key,
        filters: dims.map((d, i) => ({ field: d.field, op: 'in', values: [vals[i]], ...(d.grain ? { grain: d.grain } : {}) })),
      }));
    },
    /* Drilling is a filter on the visual that remembers what it replaced, so "Up one level" puts it back. */
    drill: (id, idx, value) => {
      setXf((x) => (x?.source === id ? null : x));
      editVisual(id, (v) => {
        const d = v.rows[idx];
        if (!d) return v;
        const nx = nextLevel(d, [...v.rows, ...v.cols]);
        const filter = { field: d.field, op: 'in', values: [value], ...(d.grain ? { grain: d.grain } : {}), drill: { at: idx, from: d } };
        return { ...v, filters: [...v.filters, filter], rows: nx ? v.rows.map((x, j) => (j === idx ? nx : x)) : v.rows, sort: null };
      });
    },
    drillUp: (id) => editVisual(id, (v) => {
      let at = -1;
      v.filters.forEach((f, i) => { if (f.drill) at = i; });
      if (at < 0) return v;
      const f = v.filters[at];
      return {
        ...v, filters: v.filters.filter((_, i) => i !== at),
        rows: v.rows[f.drill.at] ? v.rows.map((x, j) => (j === f.drill.at ? f.drill.from : x)) : v.rows, sort: null,
      };
    }),
    /* "Go to the next level" moves the whole axis down one level, every value of it, without picking one. */
    nextLevel: (id) => editVisual(id, (v) => {
      const placed = [...v.rows, ...v.cols];
      return { ...v, rows: v.rows.map((d) => nextLevel(d, placed) || d), sort: null };
    }),
    slicer: (id, s) => editVisual(id, (v) => ({ ...v, slicer: s })),
    focus: (id) => setFocusId(id),
    exportVisual: (...a) => exportRef.current?.(...a),
    editFilter: (level, index, field) => setFilterEdit({ level, index, field }),
    removeFilter: (level, i) => {
      if (level === 'report') editReport((r) => ({ ...r, filters: r.filters.filter((_, j) => j !== i) }));
      else if (level === 'page') editPage((p) => ({ ...p, filters: p.filters.filter((_, j) => j !== i) }));
      else if (sel) {
        editVisual(sel.id, (v) => {
          /* Taking away a drill step takes away the steps under it too and puts their axis back, as "Up one level"
             would — or the visual is left on a level nothing can climb back from. */
          if (!v.filters[i]?.drill) return { ...v, filters: v.filters.filter((_, j) => j !== i) };
          const gone = v.filters.map((f, j) => (j >= i && f.drill ? j : -1)).filter((j) => j >= 0);
          let rows = v.rows;
          for (const j of [...gone].reverse()) {
            const d = v.filters[j].drill;
            if (rows[d.at]) rows = rows.map((x, k) => (k === d.at ? d.from : x));
          }
          return { ...v, rows, filters: v.filters.filter((_, j) => !gone.includes(j)), sort: null };
        });
      }
    },
  }), [editing, startGesture, editVisual, editPage, editReport, addVisual, fitToType, page, sel, fieldOf, nextLevel, t]);
  /* The visuals get ONE actions object for the life of the screen, each entry calling today's version. Selecting a
     visual changes the actions above; were that object handed down, every chart would redraw on the press — and
     recharts swaps a bar's element when it redraws, so the press and the release land on two different elements and
     the browser never fires the click (measured: a click on a bar did nothing while the visual was being selected). */
  const actionsRef = useRef(actionsNow);
  actionsRef.current = actionsNow;
  const actions = useMemo(() => Object.fromEntries(Object.keys(actionsRef.current)
    .map((k) => [k, (...a) => actionsRef.current[k](...a)])), []);

  const ctx = useMemo(() => ({
    t, theme, fields, fieldOf, fieldLabel, dimLabel, valueLabel, autoTitle, filterSummary, editing, actions, fetchValues,
  }), [t, theme, fields, fieldOf, fieldLabel, dimLabel, valueLabel, autoTitle, filterSummary, editing, actions, fetchValues]);

  /* ------------------------------------------------ the questions: one per visual, a few at a time, each answer kept by
     what was asked, so going back to a page or undoing a filter costs nothing */
  const asks = useMemo(() => {
    if (!report || !page) return [];
    return page.visuals.filter((v) => v.type !== 'slicer' && v.values.length).map((v) => {
      const q = visualQuery(report, page, v, pageXf);
      return { id: v.id, key: JSON.stringify(q), q };
    });
  }, [report, page, pageXf]);
  const askKey = asks.map((a) => a.key).join('\n');
  useEffect(() => {
    const timer = setTimeout(() => {
      for (const a of asks) {
        if (inflight.current.size >= CONCURRENT) break;
        if (cache.current.has(a.key) || inflight.current.has(a.key)) continue;
        inflight.current.add(a.key);
        call('/query', { method: 'POST', body: a.q })
          .then((r) => cache.current.set(a.key, { result: r }))
          .catch((e) => cache.current.set(a.key, { error: e.message }))
          .finally(() => {
            inflight.current.delete(a.key);
            if (cache.current.size > 300) cache.current.delete(cache.current.keys().next().value);
            setTick((n) => n + 1);
          });
      }
    }, 40);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey, tick]);
  /* While a new answer is on its way the visual keeps showing the last one, marked busy, rather than going blank. */
  const answerOf = (v) => {
    const a = asks.find((x) => x.id === v.id);
    if (!a) return null;
    const hit = cache.current.get(a.key);
    if (hit) { lastByVisual.current.set(v.id, hit); return hit; }
    const last = lastByVisual.current.get(v.id);
    return last ? { ...last, busy: true } : { busy: true };
  };
  /* The day the page's own periods count from, when a range chosen above them ends before today. */
  const pageAnchor = asks.map((a) => cache.current.get(a.key)?.result?.anchor).find(Boolean) || null;
  const refresh = () => {
    cache.current.clear();
    valuesCache.current.clear();
    setTick((n) => n + 1);
  };
  /* The same geometry object for a visual that did not move, so dragging one visual does not redraw the others. */
  const geo = (id, l, tp, w, h) => {
    const k = `${l}|${tp}|${w}|${h}`;
    const c = geoCache.current.get(id);
    if (c && c.k === k) return c.g;
    const g = { left: l, top: tp, width: w, height: h };
    geoCache.current.set(id, { k, g });
    return g;
  };

  /* ------------------------------------------------ keyboard: Delete removes the selected visual, Escape lets go */
  useEffect(() => {
    const onKey = (e) => {
      if (filterEdit || dialog) return;
      const typing = e.target?.closest?.('input, textarea, select, [contenteditable]');
      if (e.key === 'Escape') {
        if (focusId) setFocusId(null);
        else if (pageXf) setXf(null);
        else setSelId(null);
      }
      if (!typing && editing && sel && e.key === 'Delete') { e.preventDefault(); actions.remove(sel.id); }
      /* Ctrl+Z / Ctrl+Y (and Ctrl+Shift+Z, ⌘ on a Mac) — but inside a box the box's own undo wins. */
      const mod = e.ctrlKey || e.metaKey;
      if (!typing && editing && mod && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) { e.preventDefault(); travel(true); }
      if (!typing && editing && mod && (e.key === 'y' || e.key === 'Y' || ((e.key === 'z' || e.key === 'Z') && e.shiftKey))) { e.preventDefault(); travel(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [filterEdit, dialog, focusId, pageXf, editing, sel, actions, travel]);

  /* ------------------------------------------------ saving */
  const reportJson = report ? JSON.stringify(report) : '';
  const dirty = current ? current.json !== reportJson : !!report;
  /* The tab keeps the work through a reload, but not through being closed: say so before it goes. */
  const guardLeave = builder && !atHome && dirty && (!current || current.canEdit);
  useEffect(() => {
    if (!guardLeave) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [guardLeave]);
  const saveReport = async (name, asNew) => {
    try {
      const body = { ...report, name: name || current?.name || report.name || '' };
      if (!asNew && current?.canEdit) {
        const r = await call(`/views/${current.id}`, { method: 'PUT', body: { spec: body, ...(name ? { name } : {}) } });
        setReport(body);
        setCurrent((c) => ({ ...c, id: r.view.id, name: r.view.name, shared: r.view.shared, json: JSON.stringify(body) }));
      } else {
        const r = await call('/views', { method: 'POST', body: { name: body.name, dataset: body.dataset, spec: body, shared: false } });
        setReport(body);
        setCurrent({ id: r.view.id, name: r.view.name, mine: true, canEdit: true, shared: false, json: JSON.stringify(body) });
        setEditing(true);
        setParams({ report: String(r.view.id) }, { replace: true });
      }
      say(t('xExplorer.saved'));
      reloadReports();
    } catch (e) { setErr(e.message); }
  };
  const newReport = (ds) => {
    const r = starterReport(ds || meta || metaList[0]);
    resetHistory();
    setReport(r);
    setPageId(r.pages[0].id);
    setCurrent(null);
    setSelId(null);
    setXf(null);
    setFocusId(null);
    setEditing(true);
    if (params.get('report') !== 'draft') setParams({ report: 'draft' });
  };

  /* ------------------------------------------------ export one visual, as it shows */
  exportRef.current = async (v, pivot, result, title) => {
    const rows = sortRows(pivot, v.sort || defaultSort(v, fields));
    const vals = result.values;
    const kind = (x) => (x.fmt === 'pct' ? 'pct' : x.fmt === 'money' || x.agg === 'avg' || x.fmt === 'num' ? 'money' : 'int');
    const colLabel = (c) => c.vals.map((x, i) => keyLabel(result.cols[i], x)).join(' · ');
    const columns = [
      ...result.rows.map((d) => ({ label: dimLabel(d), kind: 'text' })),
      ...(pivot.C
        ? [...pivot.cols.flatMap((c) => vals.map((x) => ({ label: `${colLabel(c)} · ${valueLabel(x)}`, kind: kind(x) }))),
          ...vals.map((x) => ({ label: `${t('xExplorer.total')} · ${valueLabel(x)}`, kind: kind(x) }))]
        : vals.map((x) => ({ label: valueLabel(x), kind: kind(x) }))),
    ];
    const line = (labels, cells, total) => ({ cells: [...labels, ...cells], total });
    const pickOf = (arr) => vals.map((_, m) => arr?.[m] ?? null);
    const out = rows.map((r) => line(r.vals.map((x, i) => keyLabel(result.rows[i], x)), pivot.C
      ? [...pivot.cols.flatMap((c) => pickOf(r.cells.get(c.key))), ...pickOf(r.total)]
      : pickOf(r.total)));
    if (pivot.R) {
      out.push(line([t('xExplorer.total'), ...result.rows.slice(1).map(() => '')], pivot.C
        ? [...pivot.cols.flatMap((c) => pickOf(c.total)), ...pickOf(pivot.grand)]
        : pickOf(pivot.grand), true));
    }
    const q = visualQuery(report, page, v, pageXf);
    const notes = [
      `${t('xExplorer.dataset')}: ${t(`xExplorer.ds_${report.dataset}`, { defaultValue: report.dataset })}${result.asOf ? ` · ${t('xExplorer.asOf', { date: fmtDay(result.asOf) })}` : ''}`,
      ...q.filters.map((f) => filterSummary(f)),
    ];
    try {
      const res = await fetch(`${API}/export`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, notes, columns, rows: out }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${String(title).replace(/[^\w\- ]+/g, '').trim() || 'Data Explorer'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { setErr(e.message); }
  };

  /* ------------------------------------------------ pages */
  const pageName = (p, i) => p.name || t('xExplorer.pageN', { n: i + 1 });
  /* An unnamed page is called by its place ("Page 2"); before a page moves or goes, each gets the name it shows now,
     or moving one would quietly swap the names of the others. A new page takes the next free number. */
  const named = (r) => ({ ...r, pages: r.pages.map((p, i) => (p.name ? p : { ...p, name: pageName(p, i) })) });
  const nextPageName = (r) => {
    const taken = new Set(r.pages.map((p, i) => pageName(p, i)));
    let n = r.pages.length + 1;
    while (taken.has(t('xExplorer.pageN', { n }))) n += 1;
    return t('xExplorer.pageN', { n });
  };
  const goPage = (id) => { setPageId(id); setSelId(null); setFocusId(null); };
  const addPage = () => {
    if (report.pages.length >= MAX_PAGES) return;
    const p = { ...newPage(null), name: nextPageName(report) };
    editReport((r) => ({ ...r, pages: [...r.pages, p] }));
    goPage(p.id);
  };
  const movePage = (from, to) => editReport((r0) => {
    const r = named(r0);
    const ps = [...r.pages];
    const [p] = ps.splice(from, 1);
    ps.splice(to, 0, p);
    return { ...r, pages: ps };
  });

  /* ------------------------------------------------ render */
  if (loadErr || (metaList && !metaList.length)) {
    return (
      <div className="sp-page"><Header />
        <div className="sp-main dx-page">
          <div className="pa-title"><h2>{t('xExplorer.title')}</h2></div>
          <div className="pa-banner err">{loadErr || t('xExplorer.errNoDatasets')}</div>
        </div>
      </div>
    );
  }
  const dsLabel = (key) => t(`xExplorer.ds_${key}`, { defaultValue: key });
  const copyLink = async (id) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/data-explorer?report=${id}`); } catch { /* the address bar carries it */ }
    say(t('xExplorer.linkCopied'));
  };
  const shareSaved = (view) => {
    setReports((list) => list.map((x) => (x.id === view.id ? { ...x, ...view } : x)));
    setCurrent((c) => (c && c.id === view.id ? { ...c, shared: view.shared } : c));
    setShareOf(null);
    say(t('xExplorer.shareSaved'));
  };
  const shareDialog = shareOf && (
    <ShareDialog t={t} view={shareOf} call={call} dsLabel={dsLabel(shareOf.dataset)} onCancel={() => setShareOf(null)} onSaved={shareSaved}
      onTransferred={(owner) => {
        /* the report is somebody else's now; this person stays on it as an editor */
        const id = shareOf.id;
        setShareOf(null);
        setCurrent((c) => (c && c.id === id ? { ...c, mine: false, canEdit: true, ownerName: owner.name } : c));
        reloadReports();
        say(t('xExplorer.transferDone', { name: owner.name }));
      }} />
  );
  const banners = (
    <>
      {flash && <div className="dx-flash" role="status">{flash}</div>}
      {err && (
        <div className="pa-banner err dx-err" role="alert">
          <span>{err}</span>
          <button type="button" className="dx-x" onClick={() => setErr('')} aria-label={t('xExplorer.fCancel')}><X size={13} /></button>
        </div>
      )}
    </>
  );
  if (!ready) {
    return (
      <div className="sp-page"><Header />
        <div className="sp-main dx-page"><div className="dx-wait"><Loader2 size={18} className="dx-spin" /> {t('xExplorer.loading')}</div></div>
      </div>
    );
  }
  if (atHome) {
    const dr = draftOf();
    /* Opening another report replaces the draft this browser keeps, so that is asked first. */
    const openFromHome = (v) => {
      if (dr && dr.current?.id !== v.id) setDialog({ kind: 'discard', then: v, name: dr.name || t('xExplorer.untitled') });
      else open(v);
    };
    return (
      <div className="sp-page">
        <Header />
        <div className="sp-main dx-page">
          <div className="dx-head">
            <div className="pa-title"><h2>{t('xExplorer.title')}</h2></div>
            <ModuleHelp steps={TOUR} tourKey="dataExplorer" ready={ready} />
          </div>
          {banners}
          <ExplorerHome t={t} reports={reports} builder={builder} dsName={dsLabel}
            draft={dr ? { name: dr.name } : null}
            onOpen={openFromHome}
            onNew={() => { if (dr) setDialog({ kind: 'discard', then: 'new', name: dr.name || t('xExplorer.untitled') }); else newReport(); }}
            onContinue={() => setParams({ report: dr.current ? String(dr.current.id) : 'draft' })}
            onDiscard={() => setDialog({ kind: 'discard', then: null, name: dr.name || t('xExplorer.untitled') })}
            onShare={(v) => setShareOf(v)}
            onRename={(v) => setDialog({ kind: 'renameHome', id: v.id, value: v.name })}
            onDelete={(v) => setDialog({ kind: 'deleteHome', id: v.id, name: v.name })}
            onLink={(v) => copyLink(v.id)} />
        </div>
        {shareDialog}
        {dialog?.kind === 'discard' && (
          <ConfirmDialog t={t} text={t('xExplorer.confirmDiscard', { name: dialog.name })} okLabel={t('xExplorer.discardDraft')} onCancel={() => setDialog(null)}
            onOk={() => {
              const then = dialog.then;
              setDialog(null);
              store.set('dxReport', null);
              store.set('dxCurrent', null);
              setReport(null);
              setCurrent(null);
              if (then === 'new') newReport();
              else if (then) open(then);
            }} />
        )}
        {dialog?.kind === 'renameHome' && (
          <NameDialog t={t} title={t('xExplorer.rename')} initial={dialog.value} onCancel={() => setDialog(null)}
            onOk={(name) => {
              const id = dialog.id;
              setDialog(null);
              call(`/views/${id}`, { method: 'PUT', body: { name } })
                .then((r) => { setReports((list) => list.map((x) => (x.id === id ? { ...x, ...r.view } : x))); if (current?.id === id) setCurrent((c) => ({ ...c, name: r.view.name })); })
                .catch((e) => setErr(e.message));
            }} />
        )}
        {dialog?.kind === 'deleteHome' && (
          <ConfirmDialog t={t} text={t('xExplorer.confirmDelete', { name: dialog.name })} onCancel={() => setDialog(null)}
            onOk={() => {
              const id = dialog.id;
              setDialog(null);
              call(`/views/${id}`, { method: 'DELETE' })
                .then(() => {
                  setReports((list) => list.filter((x) => x.id !== id));
                  if (current?.id === id) { setCurrent(null); setReport(null); }
                  if (store.get('dxCurrent')?.id === id) { store.set('dxReport', null); store.set('dxCurrent', null); }
                  say(t('xExplorer.deleted'));
                })
                .catch((e) => setErr(e.message));
            }} />
        )}
      </div>
    );
  }
  if (!report || !meta || !page) {
    return (
      <div className="sp-page"><Header />
        <div className="sp-main dx-page"><div className="dx-wait"><Loader2 size={18} className="dx-spin" /> {t('xExplorer.loading')}</div></div>
      </div>
    );
  }

  const shown = page.visuals.map((v) => (drag?.id === v.id ? { ...v, ...drag } : v));
  const pageH = pageHeight({ visuals: shown });
  const focused = focusId ? page.visuals.find((v) => v.id === focusId) : null;
  const dsName = t(`xExplorer.ds_${report.dataset}`, { defaultValue: report.dataset });

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main dx-page">
        <div className="dx-head">
          <div className="pa-title"><h2>{t('xExplorer.title')}</h2></div>
          <ReportsBar t={t} reports={reports} current={current} name={current?.name || report.name} dirty={dirty} editing={editing}
            builder={builder} onHome={() => { setFocusId(null); setXf(null); setParams({}); }}
            onEditing={(on) => { setEditing(on); if (!on) setSelId(null); }}
            onOpen={(v) => { if (dirty && builder && v.id !== current?.id) setDialog({ kind: 'switch', then: v }); else open(v); }}
            onNew={() => { if (dirty && builder) setDialog({ kind: 'switch', then: 'new' }); else newReport(); }}
            onSave={() => (current?.canEdit ? saveReport(null, false) : setDialog({ kind: 'name' }))}
            onSaveAs={() => setDialog({ kind: 'name', value: current ? `${current.name} (2)` : '' })}
            onRename={() => setDialog({ kind: 'name', rename: true, value: current?.name })}
            onShare={() => {
              const v = reports.find((x) => x.id === current?.id);
              if (v) setShareOf(v); else setErr(t('xExplorer.shareSaveFirst'));
            }}
            onDelete={() => setDialog({ kind: 'delete' })}
            onLink={() => copyLink(current.id)} />
        </div>
        {banners}

        <div className={`dx-studio${editing ? ' editing' : ' reading'}`}>
          <section className="dx-stage" aria-label={t('xExplorer.title')}>
            <div className="dx-toolbar">
              {editing && (
                <span className="dx-undo">
                  <button type="button" className="dx-vtool" disabled={!canUndo} onClick={() => travel(true)}
                    title={t('xExplorer.undo')} aria-label={t('xExplorer.undo')}><Undo2 size={15} /></button>
                  <button type="button" className="dx-vtool" disabled={!canRedo} onClick={() => travel(false)}
                    title={t('xExplorer.redo')} aria-label={t('xExplorer.redo')}><Redo2 size={15} /></button>
                </span>
              )}
              {metaList.length > 1 && editing ? (
                <label className="dx-param">
                  <span>{t('xExplorer.dataset')}</span>
                  <select value={report.dataset} onChange={(e) => setDialog({ kind: 'dataset', value: e.target.value })}>
                    {metaList.map((d) => <option key={d.key} value={d.key}>{t(`xExplorer.ds_${d.key}`, { defaultValue: d.key })}</option>)}
                  </select>
                </label>
              ) : <span className="dx-dsname">{t('xExplorer.dataset')}: <b>{dsName}</b></span>}
              {(meta.params || []).map((p) => (
                <label key={p.key} className="dx-param">
                  <span>{t(`xExplorer.${p.key}`)}</span>
                  <select value={report.params?.[p.key] || p.default} onChange={(e) => editReport((r) => ({ ...r, params: { ...r.params, [p.key]: e.target.value } }))}>
                    {p.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
              {(meta.switches || []).map((sw) => (
                <label key={sw.key} className="dx-switch">
                  <input type="checkbox" checked={!!report.switches?.[sw.key]}
                    onChange={(e) => editReport((r) => ({ ...r, switches: { ...r.switches, [sw.key]: e.target.checked } }))} />
                  <span>{t(`xExplorer.sw_${sw.key}`)}</span>
                </label>
              ))}
              <span className="dx-grow" />
              {pageXf && (
                <button type="button" className="dx-xfchip" onClick={() => setXf(null)}>
                  {t('xExplorer.clearSelection')} <X size={12} />
                </button>
              )}
              {pageAnchor && (
                <span className="dx-asof dx-anchor" title={t('xExplorer.anchoredHint', { date: fmtDay(pageAnchor) })}>
                  {t('xExplorer.anchored', { date: fmtDay(pageAnchor) })}
                </span>
              )}
              {meta.asOf && <span className="dx-asof">{t('xExplorer.asOf', { date: fmtDay(meta.asOf) })}</span>}
              <button type="button" className="dx-vtool" onClick={refresh} title={t('xExplorer.refresh')} aria-label={t('xExplorer.refresh')}><RefreshCw size={14} /></button>
            </div>

            {INSIGHTS_ON ? (
              <AiInsightPanel
                storageKey="aiInsight:explorer"
                askKey={asks.length ? `${current?.id || 'draft'}|${page.id}|${askKey}` : null}
                ask={({ refresh, lang }) => askInsight(`${BACKEND_URL}/api/insights/explorer`, {
                  lang, refresh, dataset: report.dataset,
                  reportName: current?.name || report.name || '', pageName: page.name || '',
                  dateFields: fields.filter((f) => f.kind === 'date').map((f) => f.key),
                  fields: fields.filter((f) => f.kind !== 'measure').map((f) => ({ key: f.key, label: fieldLabel(f.key) })),
                  filterNotes: [...(report.filters || []), ...(page.filters || [])].map((f) => filterSummary(f)),
                  /* each visual's own question, as it asks it, with the names it prints */
                  visuals: asks.map((a) => {
                    const v = page.visuals.find((x) => x.id === a.id);
                    const values = v.type === 'kpi' && v.values[0]
                      ? [v.values[0], { ...v.values[0], calc: 'py', label: undefined }]
                      : [...v.values, ...(v.type === 'combo' ? v.values2 || [] : [])];
                    return {
                      title: (v.title && String(v.title).trim()) || autoTitle(v), type: v.type, query: a.q,
                      valueLabels: values.map((x) => valueLabel(x)), dimLabels: [...a.q.rows, ...a.q.cols].map((d) => dimLabel(d)),
                    };
                  }),
                })}
              />
            ) : <span hidden data-off={INSIGHTS_OFF_MARK} />}

            <div className="dx-canvas-wrap">
              <div className="dx-sizer" ref={sizerRef}>
                {focused ? (
                  <div className="dx-focus">
                    <button type="button" className="pa-btn dx-back" onClick={() => setFocusId(null)}><Minimize2 size={14} /> {t('xExplorer.backToReport')}</button>
                    <Visual v={focused} answer={answerOf(focused)} ctx={ctx} selected={false} focus
                      geometry={{ left: 0, top: 44, right: 0, bottom: 0 }}
                      xfKey={pageXf?.source === focused.id ? pageXf.key : null} />
                  </div>
                ) : (
                  <div className="dx-canvas" style={{ height: pageH * scale, width: drawW }} data-page={page.id}
                    onPointerDown={(e) => { if (e.target === e.currentTarget) setSelId(null); }}>
                    {!page.visuals.length && <div className="dx-canvas-empty">{t(editing ? 'xExplorer.emptyPage' : 'xExplorer.emptyPageRead')}</div>}
                    {shown.map((g) => {
                      const v = page.visuals.find((x) => x.id === g.id);
                      return (
                        <Visual key={v.id} v={v} answer={answerOf(v)} ctx={ctx} selected={editing && selId === v.id}
                          geometry={geo(v.id, g.x * scale, g.y * scale, g.w * scale, g.h * scale)}
                          xfKey={pageXf?.source === v.id ? pageXf.key : null} />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="dx-pages">
              <div className="dx-ptabs" role="tablist" aria-label={t('xExplorer.pages')}>
                {report.pages.map((p, i) => (
                  <div key={p.id} className={`dx-ptab-wrap${p.id === page.id ? ' on' : ''}${editing ? ' editing' : ''}`}
                    onContextMenu={(e) => { if (!editing) return; e.preventDefault(); setPageMenu({ id: p.id, x: e.clientX, y: e.clientY }); }}>
                    <button type="button" role="tab" aria-selected={p.id === page.id}
                      className={`dx-ptab${p.id === page.id ? ' on' : ''}`}
                      onClick={() => goPage(p.id)}
                      onDoubleClick={() => { if (editing) setDialog({ kind: 'page', id: p.id, value: pageName(p, i) }); }}
                      title={editing ? t('xExplorer.pageTabHint') : undefined}>
                      {pageName(p, i)}
                    </button>
                    {editing && report.pages.length > 1 && (
                      <button type="button" className="dx-ptab-x" onClick={() => setDialog({ kind: 'deletePage', id: p.id })}
                        title={t('xExplorer.deletePage')} aria-label={`${t('xExplorer.deletePage')}: ${pageName(p, i)}`}><X size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
              {editing && (
                <>
                  <button type="button" className="dx-vtool" aria-label={t('xExplorer.pageOptions')} title={t('xExplorer.pageOptions')}
                    aria-expanded={!!pageMenu}
                    onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setPageMenu((m) => (m ? null : { id: page.id, x: r.left, y: r.top })); }}>
                    <Ellipsis size={15} />
                  </button>
                  <button type="button" className="dx-ptab add" onClick={addPage} disabled={report.pages.length >= MAX_PAGES}
                    aria-label={t('xExplorer.addPage')} title={t('xExplorer.addPage')}><Plus size={14} /></button>
                </>
              )}
            </div>
          </section>

          <FiltersPane ctx={ctx} sel={editing ? sel : null} page={page} report={report} open={panes.filters}
            onToggle={() => setPanes((p) => ({ ...p, filters: !p.filters }))} />
          {editing && (
            <>
              <VisualsPane ctx={ctx} sel={sel} open={panes.vis} onToggle={() => setPanes((p) => ({ ...p, vis: !p.vis }))} />
              <DataPane ctx={ctx} sel={sel} open={panes.data} onToggle={() => setPanes((p) => ({ ...p, data: !p.data }))} />
            </>
          )}
        </div>
      </div>

      {pageMenu && (() => {
        /* The page menu acts on the page it was opened for — the tab right-clicked, or the page on screen. */
        const mi = report.pages.findIndex((p) => p.id === pageMenu.id);
        const mp = report.pages[mi];
        if (!mp) return null;
        const close = () => setPageMenu(null);
        return (
          <div className="dx-vmenu dx-pmenu" role="menu" ref={pageMenuRef}
            style={{ left: Math.min(pageMenu.x, window.innerWidth - 220), bottom: window.innerHeight - pageMenu.y + 6 }}>
            <button type="button" role="menuitem" onClick={() => { close(); setDialog({ kind: 'page', id: mp.id, value: pageName(mp, mi) }); }}>{t('xExplorer.renamePage')}</button>
            <button type="button" role="menuitem" disabled={report.pages.length >= MAX_PAGES} onClick={() => {
              close();
              const copy = { ...JSON.parse(JSON.stringify(mp)), id: uid(), name: `${pageName(mp, mi)} (2)` };
              copy.visuals = copy.visuals.map((v) => ({ ...v, id: uid() }));
              editReport((r) => ({ ...r, pages: [...r.pages.slice(0, mi + 1), copy, ...r.pages.slice(mi + 1)] }));
              goPage(copy.id);
            }}>{t('xExplorer.duplicatePage')}</button>
            <button type="button" role="menuitem" disabled={mi === 0} onClick={() => { close(); movePage(mi, mi - 1); }}>{t('xExplorer.moveLeft')}</button>
            <button type="button" role="menuitem" disabled={mi === report.pages.length - 1} onClick={() => { close(); movePage(mi, mi + 1); }}>{t('xExplorer.moveRight')}</button>
            <button type="button" role="menuitem" className="danger" disabled={report.pages.length < 2}
              onClick={() => { close(); setDialog({ kind: 'deletePage', id: mp.id }); }}>{t('xExplorer.deletePage')}</button>
          </div>
        );
      })()}
      {filterEdit && (
        <FilterEditor t={t} datasetKey={report.dataset} field={fieldOf(filterEdit.field)} fieldLabel={fieldLabel} fetchValues={fetchValues}
          initial={filterEdit.index !== null && filterEdit.index !== undefined
            ? (filterEdit.level === 'report' ? report.filters : filterEdit.level === 'page' ? page.filters : sel?.filters || [])[filterEdit.index] || null
            : null}
          onCancel={() => setFilterEdit(null)}
          onApply={(f) => {
            const put = (list) => {
              const out = [...list];
              if (filterEdit.index !== null && filterEdit.index !== undefined) {
                if (f) out[filterEdit.index] = f; else out.splice(filterEdit.index, 1);
              } else if (f) out.push(f);
              return out;
            };
            if (filterEdit.level === 'report') editReport((r) => ({ ...r, filters: put(r.filters) }));
            else if (filterEdit.level === 'page') editPage((p) => ({ ...p, filters: put(p.filters) }));
            else if (sel) editVisual(sel.id, (v) => ({ ...v, filters: put(v.filters) }));
            setFilterEdit(null);
          }} />
      )}
      {dialog?.kind === 'name' && (
        <NameDialog t={t} title={t('xExplorer.reportName')} initial={dialog.value ?? report.name ?? ''} onCancel={() => setDialog(null)}
          onOk={(name) => {
            setDialog(null);
            if (dialog.rename) {
              const spec = { ...report, name };
              call(`/views/${current.id}`, { method: 'PUT', body: { name, spec } })
                .then((r) => { setReport(spec); setCurrent((c) => ({ ...c, name: r.view.name, json: JSON.stringify(spec) })); reloadReports(); })
                .catch((e) => setErr(e.message));
            } else saveReport(name, true);
          }} />
      )}
      {dialog?.kind === 'page' && (
        <NameDialog t={t} title={t('xExplorer.renamePage')} initial={dialog.value} okLabel={t('xExplorer.fApply')} onCancel={() => setDialog(null)}
          onOk={(name) => {
            const id = dialog.id || page.id;
            setDialog(null);
            editReport((r) => ({ ...r, pages: r.pages.map((p) => (p.id === id ? { ...p, name } : p)) }));
          }} />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog t={t} text={t('xExplorer.confirmDelete', { name: current?.name })} onCancel={() => setDialog(null)}
          onOk={async () => {
            setDialog(null);
            try {
              await call(`/views/${current.id}`, { method: 'DELETE' });
              store.set('dxReport', null);
              store.set('dxCurrent', null);
              setCurrent(null);
              setReport(null);
              setParams({}, { replace: true });
              reloadReports();
              say(t('xExplorer.deleted'));
            } catch (e) { setErr(e.message); }
          }} />
      )}
      {dialog?.kind === 'deletePage' && (
        (() => {
          const di = Math.max(0, report.pages.findIndex((p) => p.id === (dialog.id || page.id)));
          const dp = report.pages[di];
          return (
            <ConfirmDialog t={t} text={t('xExplorer.confirmDeletePage', { name: pageName(dp, di) })} onCancel={() => setDialog(null)}
              onOk={() => {
                setDialog(null);
                const rest = report.pages.filter((p) => p.id !== dp.id);
                editReport((r) => { const nr = named(r); return { ...nr, pages: nr.pages.filter((p) => p.id !== dp.id) }; });
                /* Deleting another tab leaves the page on screen where it is; deleting this one moves to its neighbour. */
                if (dp.id === page.id) goPage(rest[Math.max(0, di - 1)]?.id || null);
                say(t('xExplorer.pageDeleted'));
              }} />
          );
        })()
      )}
      {dialog?.kind === 'switch' && (
        <ConfirmDialog t={t} text={t('xExplorer.confirmDiscard', { name: current?.name || report.name || t('xExplorer.untitled') })} okLabel={t('xExplorer.discardDraft')}
          onCancel={() => setDialog(null)}
          onOk={() => { const then = dialog.then; setDialog(null); if (then === 'new') newReport(); else open(then); }} />
      )}
      {shareDialog}
      {dialog?.kind === 'dataset' && (
        <ConfirmDialog t={t} danger={false} text={t('xExplorer.confirmNewDataset')} okLabel={t('xExplorer.newReport')} onCancel={() => setDialog(null)}
          onOk={() => { const ds = metaList.find((d) => d.key === dialog.value); setDialog(null); if (ds) newReport(ds); }} />
      )}
    </div>
  );
}
